/**
 * OverdueGrowthBadge
 *
 * Count pill for the Growth nav item: how many focus areas are overdue for a
 * check-in. Renders nothing at zero — an empty circle reads as a bug, and "0"
 * is noise.
 *
 * This replaced OverdueCheckInsCard on the Dashboard. That card cost ~170px of
 * thread-sidebar height (plus a row of mobile chrome) purely to link to
 * /growth, where FocusAreaCard already shows an "Overdue" pill and the real
 * check-in button. The badge carries the same nudge from every page instead of
 * only the Dashboard, and takes no layout space.
 *
 * Deliberately a separate component rather than a hook call inside Layout:
 * Layout renders for logged-out visitors too, and hook rules would force the
 * query to run there. Mounting this only inside the authenticated branch keeps
 * the request from firing at all when there is no session.
 */

import { useOverdueCheckIns } from "@/hooks/useFocusAreaCheckIn";

/** Above this, the pill would start stretching the nav. */
const MAX_DISPLAY = 9;

interface OverdueGrowthBadgeProps {
  /** Extra classes — e.g. "ml-auto" to right-align inside a dropdown item. */
  className?: string;
}

export function OverdueGrowthBadge({ className }: OverdueGrowthBadgeProps) {
  const { data: overdueAreas } = useOverdueCheckIns();

  const count = overdueAreas?.length ?? 0;
  if (count === 0) return null;

  const label = count > MAX_DISPLAY ? `${MAX_DISPLAY}+` : String(count);

  return (
    <span
      data-testid="overdue-growth-badge"
      title={`${count} focus area${count !== 1 ? "s" : ""} overdue for a check-in`}
      className={`min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-500 dark:bg-amber-600 px-1 text-xs font-bold text-white ${className ?? ""}`}
    >
      {label}
    </span>
  );
}
